import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import { useEffect, useState } from 'react';
import { Upload } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import { settlementModalGridClass } from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import paidAmountSettlementService from '../../services/paidAmountSettlementService.js';
import { PAYMENT_MODES } from './paidAmountSettlementUi.js';

const PaidAmountSettleModal = ({
  dues,
  onClose,
  onSubmitted,
  fromDate = '',
  toDate = '',
}) => {
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [previewAllocations, setPreviewAllocations] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setAmount(String(dues?.amount_outstanding || ''));
    setPaymentMode('cash');
    setPaymentRef('');
    setRemarks('');
    setReceipt(null);
    setPreviewAllocations([]);
  }, [dues]);

  useEffect(() => {
    const payAmount = parseFloat(amount);
    if (!payAmount || payAmount <= 0) {
      setPreviewAllocations([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const params = { amount: payAmount };
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        const res = await paidAmountSettlementService.previewAllocation(params);
        setPreviewAllocations(res?.data || []);
      } catch {
        setPreviewAllocations([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [amount, fromDate, toDate]);

  const handleClose = () => {
    onClose();
  };

  const onFileChange = (e) => {
    const file = pickedFile(e);
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      Alert.error('Please upload a JPG/PNG image screenshot');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Alert.error('Image must be under 5MB');
      return;
    }
    setReceipt(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payAmount = parseFloat(amount);
    if (!payAmount || payAmount <= 0) {
      Alert.error('Enter a valid amount');
      return;
    }
    if (payAmount > Number(dues?.amount_outstanding || 0) + 0.01) {
      Alert.error('Amount cannot exceed outstanding');
      return;
    }
    if (!receipt) {
      Alert.error('Payment screenshot is required');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('amount', String(payAmount));
      fd.append('payment_mode', paymentMode);
      if (paymentRef) fd.append('payment_ref', paymentRef);
      if (remarks) fd.append('remarks', remarks);
      fd.append('receipt', receipt);
      await paidAmountSettlementService.submitRequest(fd);
      Alert.success('Submitted — AI is reviewing your receipt');
      handleClose();
      onSubmitted?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to submit settlement');
    } finally {
      setSubmitting(false);
    }
  };

  if (!dues) return null;

  return (
    <SettlementModalShell
      title="Settle paid amount"
      subtitle="Upload payment screenshot — AI will auto-approve or send for human review"
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit} className={settlementModalGridClass}>
        <div className="space-y-3">
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-amber-800">Outstanding</span>
              <span className="font-semibold text-amber-900">
                {fullCurrency(dues.amount_outstanding)}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              required
            />
          </div>
          {previewLoading ? (
            <p className="text-xs text-gray-400">Calculating shipment allocation…</p>
          ) : previewAllocations.length > 0 && (
            <div className="rounded-xl border border-sky-100 overflow-hidden">
              <div className="px-3 py-2 bg-sky-50 text-xs font-semibold text-sky-900">
                Shipments in this settlement (FIFO)
              </div>
              <div className="max-h-40 md:max-h-56 overflow-y-auto divide-y divide-sky-50">
                {previewAllocations.map((row) => (
                  <div key={row.shipment_id} className="px-3 py-2 text-xs flex justify-between gap-2">
                    <span className="font-medium text-gray-800">{row.shipment_id}</span>
                    <span className="tabular-nums text-gray-600">{fullCurrency(row.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reference</label>
            <input
              type="text"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              placeholder="UPI / UTR ref (helps AI match)"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Payment screenshot *</label>
            <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-sky-200 rounded-xl p-6">
              <Upload className="w-5 h-5 text-sky-500" />
              <span className="text-sm text-gray-500">{receipt ? receipt.name : 'Upload image'}</span>
              <MediaCaptureField mode="photo" onPick={onFileChange} />
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              AI checks amount and that payment time is after booking and before submit.
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </div>
      </form>
    </SettlementModalShell>
  );
};

export default PaidAmountSettleModal;
